import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import { useMemo, useState } from 'react';

function staffLabel(staff) {
    return staff?.full_name || staff?.title || staff?.user_profile_id || staff?.id || 'Staff member';
}

function OrgGroupFormModal({
    mode = 'create',
    group = null,
    entities = [],
    groups = [],
    staff = [],
    descendantIds = [],
    initialEntityId = '',
    initialParentGroupId = '',
    lockPlacement = false,
    canEditCode = false,
    onClose,
    onSave,
    theme = 'light'
}) {
    const dark = theme === 'dark';
    const isEdit = mode === 'edit';
    const [form, setForm] = useState({
        name: group?.name || '',
        group_type: group?.group_type || 'Department',
        code: group?.code || '',
        entity_id: group?.entity_id || initialEntityId || '',
        parent_group_id: group?.parent_group_id || initialParentGroupId || '',
        lead_staff_id: group?.lead_staff_id || ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const selectedEntityId = form.entity_id;

    const parentOptions = useMemo(
        () => groups.filter((candidate) => (
            candidate.entity_id === selectedEntityId
            && candidate.id !== group?.id
            && !descendantIds.includes(candidate.id)
        )),
        [groups, selectedEntityId, group?.id, descendantIds]
    );
    const staffOptions = useMemo(
        () => staff.filter((member) => member.entity_id === selectedEntityId),
        [staff, selectedEntityId]
    );

    const inputCls = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
    }`;
    const labelCls = `block text-sm font-medium mb-2 ${dark ? 'text-gray-300' : 'text-gray-700'}`;
    const hasLockedCode = Boolean(group?.code);
    const codeEditable = canEditCode && !hasLockedCode;

    const handleEntityChange = (value) => {
        setForm((prev) => ({
            ...prev,
            entity_id: value,
            parent_group_id: '',
            lead_staff_id: ''
        }));
    };

    const handleSubmit = async () => {
        setError(null);
        if (!form.name.trim()) { setError('Name is required.'); return; }
        if (!form.entity_id) { setError('Choose the Company/Market this group sits under.'); return; }
        const payload = {
            name: form.name.trim(),
            group_type: form.group_type.trim() || null,
            entity_id: form.entity_id,
            parent_group_id: form.parent_group_id || null,
            lead_staff_id: form.lead_staff_id || null,
            ...(codeEditable ? { code: form.code.trim() || null } : {})
        };
        try {
            setSaving(true);
            await onSave(payload);
        } catch (e) {
            setError(e?.message || 'Something went wrong while saving.');
            setSaving(false);
        }
    };

    return (
        <ModalSurface closeOnEscape={false} onClose={onClose} label="Team, group or department" overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" panelClassName={`rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto ${dark ? 'bg-gray-800' : 'bg-white'}`} panelProps={{"data-m6-layout":"entities","data-m6-component":"OrgGroupFormModal"}}>
                <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <h2 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>
                        {isEdit ? `Edit ${form.group_type}` : `New ${form.group_type}`}
                    </h2>
                    <button onClick={onClose} className={`ia-modal-desktop-close text-2xl ${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>×</button>
                </div>

                <div className="p-6 space-y-5">
                    {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className={labelCls}>Name <span className="required-asterisk">*</span></label>
                            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Dispatch Leads" className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Type</label>
                            <select value={form.group_type} onChange={(e) => set('group_type', e.target.value)} className={inputCls} disabled={isEdit}>
                                {!['Team', 'Group', 'Department'].includes(form.group_type) && (
                                    <option value={form.group_type}>{form.group_type}</option>
                                )}
                                <option value="Team">Team</option>
                                <option value="Group">Group</option>
                                <option value="Department">Department</option>
                            </select>
                            {isEdit && <p className="mt-1 text-xs text-gray-500">Group type is fixed after creation.</p>}
                        </div>
                        <div>
                            <label className={labelCls}>Code</label>
                            {hasLockedCode ? (
                                <div className={`px-3 py-2 rounded-lg border font-mono text-sm ${dark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-300 text-gray-700'}`}>
                                    {group.code}
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    name="code"
                                    value={form.code}
                                    onChange={(e) => set('code', e.target.value)}
                                    placeholder="Optional code"
                                    className={inputCls}
                                    disabled={!canEditCode}
                                />
                            )}
                            <p className={`text-xs mt-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {hasLockedCode
                                    ? 'Code is permanent once set.'
                                    : canEditCode
                                        ? 'Admins can set this once. Leave blank to set it later.'
                                        : `Only admins can set ${form.group_type} code.`}
                            </p>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelCls}>Company/Market</label>
                            <select value={form.entity_id} onChange={(e) => handleEntityChange(e.target.value)} className={inputCls} disabled={lockPlacement}>
                                <option value="">Choose a Company/Market</option>
                                {entities.filter((entity) => entity.level_type !== 'organization').map((entity) => (
                                    <option key={entity.id} value={entity.id}>{entity.name}{entity.display_label ? ` (${entity.display_label})` : ''}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelCls}>Sits under</label>
                            <select value={form.parent_group_id} onChange={(e) => set('parent_group_id', e.target.value)} className={inputCls} disabled={!selectedEntityId || lockPlacement}>
                                <option value="">— Directly under the selected Company/Market —</option>
                                {parentOptions.map((parent) => (
                                    <option key={parent.id} value={parent.id}>{parent.name}{parent.group_type ? ` (${parent.group_type})` : ''}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelCls}>Lead staff member</label>
                            <select value={form.lead_staff_id} onChange={(e) => set('lead_staff_id', e.target.value)} className={inputCls} disabled={!selectedEntityId}>
                                <option value="">— No lead assigned —</option>
                                {staffOptions.map((member) => (
                                    <option key={member.id} value={member.id}>{staffLabel(member)}</option>
                                ))}
                            </select>
                            <p className={`text-xs mt-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Staff choices are limited to the same Company/Market as this Department.</p>
                        </div>
                    </div>
                </div>

                <div className={`sticky bottom-0 border-t px-6 py-4 flex justify-end gap-3 ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <button onClick={onClose} disabled={saving} className={`px-4 py-2 rounded-lg font-medium border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}>Cancel</button>
                    <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50">
                        {saving ? 'Saving…' : isEdit ? 'Save Changes' : `Create ${form.group_type}`}
                    </button>
                </div>
            </ModalSurface>
    );
}

export default OrgGroupFormModal;
